import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { SmsIcon, SmsIconName } from '../sms/SmsMarks';

interface NavItem {
  id: string;
  to: string;
  label: string;
  icon: SmsIconName;
}

const navItems: NavItem[] = [
  { id: 'dashboard', to: '/', label: 'Главная', icon: 'home' },
  { id: 'buy-number', to: '/buy-number', label: 'Быстрая аренда', icon: 'plus' },
  { id: 'long-term-rent', to: '/long-term-rent', label: 'Долгосрочная аренда', icon: 'calendar' },
  { id: 'voice-verification', to: '/voice-verification', label: 'Voice verification', icon: 'headphones' },
  { id: 'active-numbers', to: '/active-numbers', label: 'Мои номера', icon: 'phone' },
  { id: 'operations', to: '/operations', label: 'История операций', icon: 'history' },
];

interface AppSubnavProps {
  isRefreshing: boolean;
  onRefresh: () => void;
}

const AppSubnav: React.FC<AppSubnavProps> = ({ isRefreshing, onRefresh }) => {
  const location = useLocation();
  const linksRef = useRef<HTMLDivElement>(null);
  const [scrollEdges, setScrollEdges] = useState({ left: false, right: false });
  const scrollTabs = (direction: -1 | 1) => {
    linksRef.current?.scrollBy({ left: direction * 240, behavior: 'smooth' });
  };

  useEffect(() => {
    let animationFrame = 0;
    const revealActiveLink = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        const activeLink = linksRef.current?.querySelector<HTMLElement>('.u2-tab.is-active');
        activeLink?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      });
    };

    revealActiveLink();
    window.addEventListener('resize', revealActiveLink);
    return () => {
      window.removeEventListener('resize', revealActiveLink);
      window.cancelAnimationFrame(animationFrame);
    };
  }, [location.pathname]);

  useEffect(() => {
    const element = linksRef.current;
    if (!element) return undefined;
    const updateEdges = () => {
      const maxScroll = Math.max(0, element.scrollWidth - element.clientWidth);
      setScrollEdges({
        left: element.scrollLeft > 2,
        right: element.scrollLeft < maxScroll - 2,
      });
    };
    updateEdges();
    element.addEventListener('scroll', updateEdges, { passive: true });
    window.addEventListener('resize', updateEdges);
    return () => {
      element.removeEventListener('scroll', updateEdges);
      window.removeEventListener('resize', updateEdges);
    };
  }, []);

  return (
    <div className="u2-subnav" role="navigation" aria-label="Навигация SMS">
      <div className="u2-subnavInner">
        <div className={`u2-subnavScroll ${scrollEdges.left ? 'has-left' : ''} ${scrollEdges.right ? 'has-right' : ''}`}>
          {scrollEdges.left ? <button type="button" className="u2-subnavArrow is-left" aria-label="Прокрутить вкладки влево" onClick={() => scrollTabs(-1)}><SmsIcon name="chevronLeft" /></button> : null}
          <div className="u2-subnavLinks" ref={linksRef}>
            {navItems.map((item) => (
              <NavLink
                key={item.id}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) => `u2-tab ${isActive ? 'is-active' : ''}`}
              >
                <SmsIcon name={item.icon} />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>
          {scrollEdges.right ? <button type="button" className="u2-subnavArrow is-right" aria-label="Прокрутить вкладки вправо" onClick={() => scrollTabs(1)}><SmsIcon name="chevronRight" /></button> : null}
        </div>

        <button
          type="button"
          className="u2-tab u2-subnavRefresh"
          onClick={onRefresh}
          disabled={isRefreshing}
          aria-label={isRefreshing ? 'Данные обновляются' : 'Обновить данные'}
        >
          <SmsIcon
            name="refresh"
            className={isRefreshing ? 'is-spinning' : undefined}
          />
          <span>{isRefreshing ? 'Обновляем…' : 'Обновить'}</span>
        </button>
      </div>
    </div>
  );
};

export default AppSubnav;
